import React, { useRef, useState } from "react";
import { HiX, HiCheck } from "react-icons/hi";
import {
  ManualHealthDraft,
  respRateInputText,
  sleepInput,
  validateManualHealthLog,
  vitalInputText,
} from "./healthLogDraft";

interface AddHealthLogFormProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (
    healthLog: ManualHealthDraft & { logDate?: string },
  ) => void | Promise<void>;
  dateID: string;
  editMode?: boolean;
  initialData?: {
    hrv?: number;
    respRate?: number;
    rhr?: number;
    sleepDuration?: number;
    userFields?: string[];
  };
  logId?: string;
}

const AddHealthLogForm: React.FC<AddHealthLogFormProps> = ({
  open,
  onClose,
  onSubmit,
  dateID,
  editMode = false,
  initialData,
}) => {
  const [hrv, setHrv] = useState(vitalInputText(initialData?.hrv));
  const [rhr, setRhr] = useState(vitalInputText(initialData?.rhr));
  const [respRate, setRespRate] = useState(
    respRateInputText(initialData?.respRate),
  );
  const [sleepHours, setSleepHours] = useState(
    sleepInput(initialData?.sleepDuration).hours,
  );
  const [sleepMinutes, setSleepMinutes] = useState(
    sleepInput(initialData?.sleepDuration).minutes,
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showInfoDialog, setShowInfoDialog] = useState(false);

  const dateIDToInputValue = (id: string): string => {
    if (!id || id.length < 8) return "";
    return `${id.substring(0, 4)}-${id.substring(4, 6)}-${id.substring(6, 8)}`;
  };
  const inputValueToDateID = (val: string): string => val.replace(/-/g, "");

  const [dateInput, setDateInput] = useState<string>(
    dateIDToInputValue(dateID),
  );
  const dateInputRef = useRef<HTMLInputElement>(null);

  const openDatePicker = () => {
    const el = dateInputRef.current as
      | (HTMLInputElement & { showPicker?: () => void })
      | null;
    if (!el) return;
    try {
      if (typeof el.showPicker === "function") {
        el.showPicker();
      } else {
        el.focus();
      }
    } catch {
      el.focus();
    }
  };

  const formatDate = (id: string) => {
    const year = parseInt(id.substring(0, 4));
    const month = parseInt(id.substring(4, 6));
    const day = parseInt(id.substring(6, 8));
    const date = new Date(year, month - 1, day);

    return date.toLocaleDateString("en-US", {
      weekday: "long",
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  };

  const validation = validateManualHealthLog(
    { hrv, rhr, respRate, sleepHours, sleepMinutes },
    initialData,
  );

  const resetFields = () => {
    if (editMode && initialData) {
      const sleep = sleepInput(initialData.sleepDuration);
      setHrv(vitalInputText(initialData.hrv));
      setRhr(vitalInputText(initialData.rhr));
      setRespRate(respRateInputText(initialData.respRate));
      setSleepHours(sleep.hours);
      setSleepMinutes(sleep.minutes);
    } else {
      setHrv("");
      setRhr("");
      setRespRate("");
      setSleepHours("");
      setSleepMinutes("");
    }
  };

  const handleSubmit = async () => {
    if (!validation.canSubmit) return;
    if (!validation.draft) {
      handleClose();
      return;
    }

    setIsSubmitting(true);
    setError(null);
    try {
      await onSubmit({
        ...validation.draft,
        logDate: inputValueToDateID(dateInput) || dateID,
      });
      handleClose();
    } catch (submitError) {
      console.error("Error adding health log:", submitError);
      setError(
        editMode
          ? "Failed to update health log. Please try again."
          : "Failed to add health log. Please try again.",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    resetFields();
    setError(null);
    onClose();
  };

  React.useEffect(() => {
    if (!open) return;
    if (editMode && initialData) {
      const sleep = sleepInput(initialData.sleepDuration);
      setHrv(vitalInputText(initialData.hrv));
      setRhr(vitalInputText(initialData.rhr));
      setRespRate(respRateInputText(initialData.respRate));
      setSleepHours(sleep.hours);
      setSleepMinutes(sleep.minutes);
    } else if (!editMode) {
      setHrv("");
      setRhr("");
      setRespRate("");
      setSleepHours("");
      setSleepMinutes("");
    }
    setDateInput(dateIDToInputValue(dateID));
    setError(null);
  }, [editMode, initialData, open, dateID]);

  const handleIntegerChange =
    (setter: (value: string) => void) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setter(e.target.value.replace(/\D/g, ""));
    };

  const handleRespChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const cleaned = e.target.value.replace(",", ".").replace(/[^0-9.]/g, "");
    const parts = cleaned.split(".");
    if (parts.length > 2) return;
    if (parts.length === 2) {
      setRespRate(`${parts[0]}.${parts[1].slice(0, 1)}`);
      return;
    }
    setRespRate(cleaned);
  };

  if (!open) return null;

  const displayDateID = inputValueToDateID(dateInput) || dateID;
  const visibleError = validation.error || error;

  return (
    <div
      className="fixed inset-0 z-[9999] bg-black/40 backdrop-blur-sm overflow-y-auto"
      style={{ transform: "translateZ(0)" }}
    >
      <div className="flex min-h-screen md:items-center md:justify-center items-start justify-center">
        <div className="w-full max-w-2xl mx-auto overflow-hidden rounded-none md:rounded-md bg-base-100 text-left align-middle shadow-xl min-h-screen md:min-h-0 md:max-h-[90vh] flex flex-col">
          <div className="flex justify-between items-start p-6 flex-shrink-0">
            <div>
              <div className="flex items-center gap-1">
                <h2 className="text-lg font-grotesk">Health Stats</h2>
                <button
                  onClick={() => setShowInfoDialog(true)}
                  className="p-1 hover:bg-base-200 rounded-full transition-colors"
                  type="button"
                >
                  <span className="w-4 h-4 flex items-center justify-center text-base-content/50 hover:text-base-content/80 text-xs font-bold">
                    ?
                  </span>
                </button>
              </div>
              <div className="relative mt-1">
                <button
                  type="button"
                  onClick={openDatePicker}
                  className="flex items-center gap-1.5 text-sm text-base-content/60 hover:text-base-content transition-colors"
                >
                  <span className="font-medium underline decoration-dotted underline-offset-4">
                    {formatDate(displayDateID)}
                  </span>
                </button>
                <input
                  ref={dateInputRef}
                  type="date"
                  value={dateInput}
                  onChange={(e) => setDateInput(e.target.value)}
                  className="absolute left-0 top-6 w-0 h-0 opacity-0 pointer-events-none"
                  tabIndex={-1}
                  aria-hidden="true"
                />
              </div>
            </div>
            <button
              onClick={handleClose}
              className="bg-base-200 p-2 rounded-full"
            >
              <HiX className="w-5 h-5 text-base-content/80" />
            </button>
          </div>

          <div
            className="flex-1 overflow-y-auto p-8 md:p-6 space-y-6"
            style={{
              WebkitOverflowScrolling: "touch",
              touchAction: "pan-y",
              transform: "translateZ(0)",
              maxHeight: "calc(100vh - 200px)",
              minHeight: "0",
            }}
          >
            <p className="text-sm text-base-content/60">
              Enter any metric. At least one is required.
            </p>

            {visibleError && (
              <div className="alert alert-error">
                <span>{visibleError}</span>
              </div>
            )}

            <div className="mb-3">
              <div className="new-form-group w-full">
                <input
                  type="text"
                  className="form-element"
                  placeholder="ms"
                  value={hrv}
                  onChange={handleIntegerChange(setHrv)}
                  inputMode="numeric"
                  autoFocus
                />
                <label className="text-base-content/80">HRV</label>
              </div>
            </div>

            <div className="mb-3">
              <div className="new-form-group w-full">
                <input
                  type="text"
                  className="form-element"
                  placeholder="bpm"
                  value={rhr}
                  onChange={handleIntegerChange(setRhr)}
                  inputMode="numeric"
                />
                <label className="text-base-content/80">
                  Resting heart rate
                </label>
              </div>
            </div>

            <div className="mb-3">
              <div className="new-form-group w-full">
                <input
                  type="text"
                  className="form-element"
                  placeholder="breaths / min"
                  value={respRate}
                  onChange={handleRespChange}
                  inputMode="decimal"
                />
                <label className="text-base-content/80">Respiratory rate</label>
              </div>
            </div>

            <div className="mb-3">
              <div className="flex gap-2">
                <div className="flex-1">
                  <div className="new-form-group w-full">
                    <input
                      type="text"
                      className="form-element"
                      placeholder="h"
                      value={sleepHours}
                      onChange={handleIntegerChange(setSleepHours)}
                      inputMode="numeric"
                    />
                    <label className="text-base-content/80">Sleep hours</label>
                  </div>
                </div>
                <div className="flex-1">
                  <div className="new-form-group w-full">
                    <input
                      type="text"
                      className="form-element"
                      placeholder="m"
                      value={sleepMinutes}
                      onChange={handleIntegerChange(setSleepMinutes)}
                      inputMode="numeric"
                    />
                    <label className="text-base-content/80">
                      Sleep minutes
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="px-5 pb-5 pt-28 -mt-24 flex-shrink-0 relative z-50 flex items-center justify-end gap-3 bg-gradient-to-t from-base-100 from-30% to-transparent pointer-events-none">
            <button
              onClick={handleSubmit}
              disabled={!validation.canSubmit || isSubmitting}
              aria-label={editMode ? "Update health stats" : "Add health stats"}
              title={editMode ? "Update health stats" : "Add health stats"}
              className="pointer-events-auto w-14 h-14 rounded-full bg-primary text-white flex items-center justify-center shadow-lg transition-opacity disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {isSubmitting ? (
                <span className="loading loading-spinner loading-sm" />
              ) : (
                <HiCheck className="w-6 h-6" />
              )}
            </button>
          </div>
        </div>
      </div>

      {showInfoDialog && (
        <div
          className="fixed inset-0 z-[10000] bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setShowInfoDialog(false)}
        >
          <div
            className="bg-base-100 rounded-lg max-w-lg w-full max-h-[80vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-8">
              <div className="flex justify-between items-start mb-4">
                <h3 className="text-lg font-bold font-grotesk text-base-content">
                  Health & Sleep Stats
                </h3>
                <button
                  onClick={() => setShowInfoDialog(false)}
                  className="p-1 hover:bg-base-200 rounded-full transition-colors"
                >
                  <HiX className="w-5 h-5 text-base-content/60" />
                </button>
              </div>

              <div className="space-y-4 text-sm text-base-content/80">
                <div>
                  <h4 className="font-semibold text-base-content mb-1 font-inter">
                    Heart Rate Variability (HRV)
                  </h4>
                  <p className="font-inter">
                    HRV measures the variation in time between heartbeats and is
                    a key indicator of your body's recovery status. Higher HRV
                    typically indicates better recovery and readiness to train,
                    while lower HRV may suggest you need more rest.
                  </p>
                </div>

                <div>
                  <h4 className="font-semibold text-base-content mb-1 font-inter">
                    Resting Heart Rate (RHR)
                  </h4>
                  <p className="font-inter">
                    Your RHR is the number of heartbeats per minute while at
                    complete rest. A lower RHR generally indicates better
                    cardiovascular fitness. Tracking changes helps identify
                    overtraining, illness, or improved fitness levels.
                  </p>
                </div>

                <div>
                  <h4 className="font-semibold text-base-content mb-1 font-inter">
                    Respiratory Rate (RR)
                  </h4>
                  <p className="font-inter">
                    Respiratory rate is the number of breaths you take per
                    minute. Changes in your normal breathing pattern can
                    indicate stress, illness, or changes in fitness. It's an
                    often-overlooked metric that provides valuable insights into
                    your overall health.
                  </p>
                </div>

                <div>
                  <h4 className="font-semibold text-base-content mb-1 font-inter">
                    Sleep Duration
                  </h4>
                  <p className="font-inter">
                    Quality sleep is essential for recovery, performance, and
                    overall health. Tracking sleep duration helps you understand
                    how rest impacts your training and identify patterns that
                    affect your performance.
                  </p>
                </div>
              </div>

              <div className="mt-6">
                <button
                  onClick={() => setShowInfoDialog(false)}
                  className="w-full bg-neutral text-white py-3 px-6 font-grotesk font-bold rounded-md hover:bg-neutral transition-colors"
                >
                  Got it
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AddHealthLogForm;
